import { useMemo } from 'react';
import {
  announce as busAnnounce,
  clear as busClear,
  type AnnounceOptions,
  type Politeness,
} from './announceBus';

/**
 * Everything about a message EXCEPT how the AT delivers it — which is the part
 * the politeness helpers already decided.
 *
 * The helpers take this so adding an axis to `AnnounceOptions` widens them
 * automatically. Fixing the politeness and leaving no room for anything else is
 * what makes a convenience wrapper turn into a dead end: the first caller that
 * needs the new field drops to the general `announce`, and the helper quietly
 * stops being used rather than being improved.
 */
export type AnnounceDetails = Omit<AnnounceOptions, 'politeness'>;

export interface UseAnnouncerResult {
  announce: (message: string, options?: AnnounceOptions) => void;
  announcePolite: (message: string, details?: AnnounceDetails) => void;
  announceAssertive: (message: string, details?: AnnounceDetails) => void;
  clear: (politeness?: Politeness) => void;
}

export function useAnnouncer(): UseAnnouncerResult {
  return useMemo<UseAnnouncerResult>(
    () => ({
      announce: busAnnounce,
      announcePolite: (message, details) =>
        busAnnounce(message, { ...details, politeness: 'polite' }),
      announceAssertive: (message, details) =>
        busAnnounce(message, { ...details, politeness: 'assertive' }),
      clear: busClear,
    }),
    []
  );
}
